import { useEffect, useState } from 'react'
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import 'dayjs/locale/pt-br'
import dayjs, { Dayjs } from 'dayjs'
import ContentDialog from '../../../components/Dialogs/ContentDialog'
import { TProduct, TProductSimple } from '../../../core/types/Product'
import ProductController from '../../../core/controllers/ProductController'
import { IErrorResponse } from '../../../core/types/ErrorResponse'

interface Props {
  open: boolean
  product: TProduct | null
  onClose: () => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export default function ResendMemberAccessDialog({
  open,
  product,
  onClose,
  onSuccess,
  onError,
}: Props) {
  const [products, setProducts] = useState<TProductSimple[]>([])
  const [originProduct, setOriginProduct] = useState<TProductSimple | null>(
    null
  )
  const [dateFilter, setDateFilter] = useState<Dayjs | null>(null)

  useEffect(() => {
    if (!open) return
    setOriginProduct(null)
    setDateFilter(null)
    ProductController.getList({}).then((result) => {
      const err = result as IErrorResponse
      if (!err.error) {
        setProducts(result as TProductSimple[])
      }
    })
  }, [open])

  const handleConfirm = async (): Promise<boolean> => {
    if (!product) return false
    const result = await ProductController.resendMemberAccess({
      destino: { productId: product.id },
      origem: originProduct ? { productId: originProduct.id } : undefined,
      dateFilter: dateFilter ? dateFilter.toISOString() : null,
    })
    if (result.success) {
      onSuccess('Acessos reenviados com sucesso')
      return true
    }
    onError(result.error || 'Erro ao reenviar acessos')
    return false
  }

  return (
    <ContentDialog
      open={open}
      title="Reenviar acessos"
      subtitle={product?.name}
      onClose={onClose}
      labelPrositive="Confirmar"
      hasCancel
      onPositive={handleConfirm}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Reenvia os acessos dos alunos para a configuração atual do produto.
        </Typography>
        <LocalizationProvider adapterLocale="pt-br" dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Filtro de data (opcional)"
            maxDateTime={dayjs()}
            value={dateFilter}
            onChange={(v) => setDateFilter(v)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText: 'Filtra apenas pedidos anteriores à data informada',
              },
            }}
          />
        </LocalizationProvider>
        <Autocomplete
          options={products}
          getOptionLabel={(o) => o.name}
          value={originProduct}
          onChange={(_, v) => setOriginProduct(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Produto de origem (opcional)"
              size="small"
              helperText="Se vazio, usa os alunos do próprio produto destino"
            />
          )}
        />
      </Stack>
    </ContentDialog>
  )
}
